import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
    Box,
    Button,
    Chip,
    Stack,
    Card,
    CardContent,
    Alert,
    LinearProgress,
    IconButton,
    Paper,
    Badge,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    useTheme,
    alpha
} from '@mui/material';
import {
    PhotoCamera as PhotoCameraIcon,
    AttachFile as AttachFileIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    LocationOn as LocationIcon,
    Science as ScienceIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../../../supabaseClient';

export default function SamplingStep({ 
    formData, 
    setFormData, 
    onValidationChange,
    showSnackbar,
    batchTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const [dragOver, setDragOver] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [samplingComplete, setSamplingComplete] = useState(false);
    const [filePreviewOpen, setFilePreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({});

    // Comprehensive validation logic
    const validateSampling = () => {
        const errors = {};
        
        if (!formData.sample_location) {
            errors.sample_location = 'Sample location is required';
        }
        
        if (!formData.tested_by_name) {
            errors.tested_by_name = 'Tester name is required';
        }
        
        if (!formData.test_datetime) {
            errors.test_datetime = 'Test date and time is required';
        }

        // Validate test date (not in future, not too old)
        if (formData.test_datetime) {
            const testDate = dayjs(formData.test_datetime);
            const now = dayjs();
            
            if (testDate.isAfter(now)) {
                errors.future_date = 'Test date cannot be in the future';
            }
            
            if (testDate.isBefore(now.subtract(30, 'days'))) {
                errors.old_date = 'Warning: Test date is more than 30 days old';
            }
        }

        // Sample quantity validation
        if (formData.sample_quantity && isNaN(parseFloat(formData.sample_quantity))) {
            errors.sample_quantity = 'Sample quantity must be a valid number';
        }

        setValidationErrors(errors);
        const isValid = Object.keys(errors).filter(key => !key.includes('warning') && !key.includes('old_date')).length === 0;
        setSamplingComplete(isValid);
        
        return isValid;
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            const isValid = validateSampling();
            if (onValidationChange) {
                onValidationChange('sampling', isValid);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [
        formData.sample_location,
        formData.tested_by_name,
        formData.test_datetime,
        formData.sample_quantity
    ]);

    const handleFileUpload = async (files, type) => {
        const fileArray = Array.from(files);
        const maxSize = type === 'photos' ? 10 * 1024 * 1024 : 20 * 1024 * 1024; // 10MB for photos, 20MB for docs
        const bucketName = type === 'photos' ? 'production-quality-control-sample-photos' : 'production-quality-control-documents';
        const validFiles = [];
        
        for (const file of fileArray) {
            if (file.size > maxSize) {
                showSnackbar?.(`File ${file.name} is too large (max ${maxSize / 1024 / 1024}MB)`, 'error');
                continue;
            }
            
            try {
                // Set upload progress
                setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
                
                // Generate secure file path using the database function
                const { data: pathData, error: pathError } = await supabase
                    .rpc('generate_qc_file_path', {
                        file_type: type.slice(0, -1), // Remove 's' from 'photos'/'documents'
                        file_name: file.name,
                        test_id: formData.id || null
                    });
                
                if (pathError) {
                    throw new Error(`Path generation failed: ${pathError.message}`);
                }
                
                const filePath = pathData;
                
                // Update progress
                setUploadProgress(prev => ({ ...prev, [file.name]: 25 }));
                
                // Upload file to Supabase Storage
                const { data: uploadData, error: uploadError } = await supabase.storage
                    .from(bucketName)
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: false
                    });
                
                if (uploadError) {
                    throw new Error(`Upload failed: ${uploadError.message}`);
                }
                
                // Update progress
                setUploadProgress(prev => ({ ...prev, [file.name]: 75 }));
                
                // Get public URL for the uploaded file
                const { data: urlData } = supabase.storage
                    .from(bucketName)
                    .getPublicUrl(filePath);
                
                // Create file object with metadata
                const fileObj = {
                    id: Date.now() + Math.random(),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    uploadDate: dayjs().toISOString(),
                    url: urlData.publicUrl,
                    filePath: filePath,
                    bucketName: bucketName,
                    supabaseId: uploadData.id
                };
                
                validFiles.push(fileObj);
                
                // Complete progress
                setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
                
            } catch (error) {
                showSnackbar?.(`Failed to upload ${file.name}: ${error.message}`, 'error');
            }
        }
        
        if (validFiles.length > 0) {
            if (type === 'photos') {
                setFormData(prev => ({
                    ...prev,
                    photo_urls: [...(prev.photo_urls || []), ...validFiles]
                }));
                showSnackbar?.(`Uploaded ${validFiles.length} photo(s) successfully`, 'success');
            } else {
                setFormData(prev => ({
                    ...prev,
                    document_urls: [...(prev.document_urls || []), ...validFiles]
                }));
                showSnackbar?.(`Uploaded ${validFiles.length} document(s) successfully`, 'success');
            }
        }
        
        // Clear progress after delay
        setTimeout(() => {
            setUploadProgress(prev => {
                const newProgress = { ...prev };
                fileArray.forEach(file => delete newProgress[file.name]);
                return newProgress;
            });
        }, 2000);
    };

    const removeFile = async (index, type) => {
        const currentFiles = type === 'photos' ? [...(formData.photo_urls || [])] : [...(formData.document_urls || [])];
        const fileToRemove = currentFiles[index];
        
        if (!fileToRemove) return;
        
        try {
            // Delete from Supabase Storage if it has a file path (uploaded file)
            if (fileToRemove.filePath && fileToRemove.bucketName) {
                const { error: deleteError } = await supabase.storage
                    .from(fileToRemove.bucketName)
                    .remove([fileToRemove.filePath]);
                
                if (deleteError) {
                    showSnackbar?.(`Warning: File removed from form but may still exist in storage`, 'warning');
                }
            }
            
            // Remove from form data
            currentFiles.splice(index, 1);
            
            if (type === 'photos') {
                setFormData(prev => ({...prev, photo_urls: currentFiles}));
                showSnackbar?.(`Removed photo: ${fileToRemove.name}`, 'info');
            } else {
                setFormData(prev => ({...prev, document_urls: currentFiles}));
                showSnackbar?.(`Removed document: ${fileToRemove.name}`, 'info');
            }
            
        } catch (error) {
            showSnackbar?.(`Error removing file: ${error.message}`, 'error');
        }
    };

    const handleDrop = (e, type) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileUpload(files, type);
        }
    };

    const handleSampleLocationChange = (value) => {
        setFormData(prev => ({...prev, sample_location: value}));
    };

    const handleSampleIdChange = (value) => {
        setFormData(prev => ({...prev, sample_id: value}));
    };

    const handleSampleQuantityChange = (value) => {
        setFormData(prev => ({...prev, sample_quantity: value}));
    };

    const handleSampleUnitChange = (value) => {
        setFormData(prev => ({...prev, sample_unit: value}));
    };

    const handleRetentionLocationChange = (value) => {
        setFormData(prev => ({...prev, sample_retention_location: value}));
    };

    const handleTestedByNameChange = (value) => {
        setFormData(prev => ({...prev, tested_by_name: value}));
    };

    const handleTestedByRoleChange = (value) => {
        setFormData(prev => ({...prev, tested_by_role: value}));
    };

    const handleTestDateTimeChange = (value) => {
        setFormData(prev => ({...prev, test_datetime: value}));
    };

    const handleNotesChange = (value) => {
        setFormData(prev => ({...prev, notes: value}));
    };

    const generateSampleId = () => {
        const today = dayjs().format('YYYYMMDD');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const sampleId = `SMPL-${today}-${random}`;
        setFormData(prev => ({...prev, sample_id: sampleId}));
        showSnackbar?.(`Generated sample ID: ${sampleId}`, 'success');
    };

    const handlePreviewFile = async (file) => {
        try {
            // If it's a Supabase stored file, generate a signed URL for secure access
            if (file.filePath && file.bucketName && !file.url.includes('blob:')) {
                const { data: signedUrlData, error: urlError } = await supabase.storage
                    .from(file.bucketName)
                    .createSignedUrl(file.filePath, 3600); // 1 hour expiry
                
                if (urlError) {
                    showSnackbar?.('Unable to preview file', 'error');
                    return;
                }
                
                setPreviewFile({
                    ...file,
                    url: signedUrlData.signedUrl
                });
            } else {
                setPreviewFile(file);
            }
            
            setFilePreviewOpen(true);
        } catch (error) {
            showSnackbar?.('Error opening file preview', 'error');
        }
    };

    // Get sampling completion percentage
    const getCompletionPercentage = () => {
        const requiredFields = ['sample_location', 'tested_by_name', 'test_datetime'];
        const completedFields = requiredFields.filter(field => formData[field]);
        return (completedFields.length / requiredFields.length) * 100;
    };

    const completionPercentage = getCompletionPercentage();



    return (
        <Box sx={{ p: 4 }}>
            {/* Header with Progress */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            mr: 1
                        }}>
                            <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                Sampling & Execution
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Record sample information, test execution details, and supporting documentation
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: samplingComplete ? theme.palette.success.main : theme.palette.warning.main }}>
                                {Math.round(completionPercentage)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Complete
                            </Typography>
                        </Box>
                    </Stack>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={completionPercentage} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[400], 0.3),
                            '& .MuiLinearProgress-bar': {
                                background: samplingComplete 
                                    ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                                    : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`
                            }
                        }} 
                    />
                </CardContent>
            </Card>
            
            <Grid container spacing={4}>

            {/* Validation Alerts */}
            {Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12}>
                    <Alert 
                        severity={validationErrors.future_date ? "error" : "warning"}
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: validationErrors.future_date 
                                ? alpha(theme.palette.error.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: validationErrors.future_date 
                                    ? theme.palette.error.main
                                    : theme.palette.warning.main
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            mb: 1, 
                            color: validationErrors.future_date 
                                ? theme.palette.error.main
                                : theme.palette.warning.main
                        }}>
                            Sampling Issues:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {Object.values(validationErrors).map((error, index) => (
                                <li key={index}>
                                    <Typography variant="body2" sx={{ 
                                        color: validationErrors.future_date 
                                            ? theme.palette.error.main
                                            : theme.palette.warning.main,
                                        fontWeight: 500 
                                    }}>{error}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                </Grid>
            )}

            {/* Sample Information Section */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <LocationIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Sample Information
                            </Typography>
                        </Stack>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Sample ID"
                                    value={formData.sample_id || ''}
                                    onChange={(e) => handleSampleIdChange(e.target.value)}
                                    placeholder="e.g., SMPL-2024-001"
                                    InputProps={{
                                        endAdornment: (
                                            <Button
                                                size="small"
                                                onClick={generateSampleId}
                                                sx={{ 
                                                    fontSize: '0.8rem', 
                                                    fontWeight: 500,
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                                                    color: 'white',
                                                    borderRadius: 2,
                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                    '&:hover': {
                                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.7)})`
                                                    }
                                                }}
                                            >
                                                Generate
                                            </Button>
                                        )
                                    }}
                                    InputLabelProps={{
                                        sx: { fontWeight: 500 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& input': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Sample Location *"
                                    name="sample_location"
                                    value={formData.sample_location || ''}
                                    onChange={(e) => handleSampleLocationChange(e.target.value)}
                                    placeholder="e.g., Mixing Tank 1, Tank Outlet, Production Floor, QC Lab"
                                    error={!!validationErrors.sample_location}
                                    helperText={validationErrors.sample_location || "Where was the sample taken?"}
                                    required
                                    InputLabelProps={{
                                        sx: { fontWeight: 500 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& input': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Sample Retention Location"
                                    value={formData.sample_retention_location || ''}
                                    onChange={(e) => handleRetentionLocationChange(e.target.value)}
                                    placeholder="e.g., QC Lab Refrigerator, Shelf A3-12"
                                    helperText="Where is the sample stored for reference?"
                                    InputLabelProps={{
                                        sx: { fontWeight: 500 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& input': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Sample Quantity"
                                    type="number"
                                    value={formData.sample_quantity || ''}
                                    onChange={(e) => handleSampleQuantityChange(e.target.value)}
                                    placeholder="Amount collected"
                                    error={!!validationErrors.sample_quantity}
                                    helperText={validationErrors.sample_quantity}
                                    InputLabelProps={{
                                        sx: { fontWeight: 500 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& input': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ fontWeight: 500 }}>Sample Unit</InputLabel>
                                    <Select
                                        value={formData.sample_unit || 'ml'}
                                        label="Sample Unit"
                                        onChange={(e) => handleSampleUnitChange(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& .MuiSelect-select': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }}
                                    >
                                        <MenuItem value="ml" sx={{ py: 2, fontWeight: 500 }}>ml</MenuItem>
                                        <MenuItem value="g" sx={{ py: 2, fontWeight: 500 }}>g</MenuItem>
                                        <MenuItem value="oz" sx={{ py: 2, fontWeight: 500 }}>oz</MenuItem>
                                        <MenuItem value="units" sx={{ py: 2, fontWeight: 500 }}>units</MenuItem>
                                        <MenuItem value="L" sx={{ py: 2, fontWeight: 500 }}>L</MenuItem>
                                        <MenuItem value="kg" sx={{ py: 2, fontWeight: 500 }}>kg</MenuItem>
                                        <MenuItem value="lb" sx={{ py: 2, fontWeight: 500 }}>lb</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            
            {/* Test Execution Section */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <PersonIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Test Execution
                            </Typography>
                        </Stack>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Tested By *"
                                    value={formData.tested_by_name || ''}
                                    onChange={(e) => handleTestedByNameChange(e.target.value)}
                                    placeholder="Full name of tester"
                                    error={!!validationErrors.tested_by_name}
                                    helperText={validationErrors.tested_by_name || "Who performed this test?"}
                                    required
                                    InputLabelProps={{
                                        sx: { fontWeight: 500 }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& input': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth required>
                                    <InputLabel sx={{ fontWeight: 500 }}>Tester Role</InputLabel>
                                    <Select
                                        value={formData.tested_by_role || 'qa_technician'}
                                        label="Tester Role"
                                        onChange={(e) => handleTestedByRoleChange(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                border: `1px solid ${theme.palette.primary.main}`
                                            },
                                            '& .MuiSelect-select': {
                                                py: 2,
                                                fontWeight: 500
                                            },
                                            backgroundColor: 'white',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }}
                                    >
                                        <MenuItem value="operator" sx={{ py: 2 }}>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Operator</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                    Production line operator
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="qa_technician" sx={{ py: 2 }}>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>QA Technician</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                    Quality assurance technician
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="qa_analyst" sx={{ py: 2 }}>
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>QA Analyst</Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                                    Quality assurance analyst
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <DateTimePicker
                                    label="Test Date & Time *"
                                    value={formData.test_datetime}
                                    onChange={(newValue) => handleTestDateTimeChange(newValue)}
                                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                    ampm={batchTimeFormat !== '24hour'}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                            error: !!validationErrors.test_datetime || !!validationErrors.future_date,
                                            helperText: validationErrors.test_datetime || validationErrors.future_date,
                                            InputLabelProps: {
                                                sx: { fontWeight: 500 }
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                        borderRadius: 3
                                                    },
                                                    '&:hover fieldset': {
                                                        border: `1px solid ${theme.palette.primary.main}`
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        border: `1px solid ${theme.palette.primary.main}`
                                                    },
                                                    '& input': {
                                                        py: 2,
                                                        fontWeight: 500
                                                    },
                                                    backgroundColor: 'white',
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    backdropFilter: 'blur(10px)',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    '&:hover': {
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes & Observations"
                    value={formData.notes || ''}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Additional observations, test conditions, deviations, or relevant information..."
                    helperText="Document any unusual conditions, observations, or deviations during testing"
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& textarea': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12}>
                <Box sx={{ my: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <CloudUploadIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Documentation & Files
                        </Typography>
                        <Chip 
                            label={`${(formData.photo_urls?.length || 0) + (formData.document_urls?.length || 0)} files`}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                        />
                    </Stack>
                </Box>
            </Grid>
            
            {/* Photo Upload */}
            <Grid item xs={12} md={6}>
                <Box 
                    sx={{ 
                        p: 3, 
                        border: dragOver ? '1px dashed #2196f3' : '1px dashed #ccc', 
                        borderRadius: 2,
                        textAlign: 'center',
                        backgroundColor: dragOver ? '#f3f8ff' : '#f9f9f9',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                    }}
                    onDrop={(e) => handleDrop(e, 'photos')}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onClick={() => document.getElementById('photo-upload').click()}
                >
                    <PhotoCameraIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        Upload Photos
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Drag & drop or click to select multiple photos
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Supports: JPG, PNG, WEBP (Max 10MB each)
                    </Typography>
                    <input
                        id="photo-upload"
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files, 'photos')}
                    />
                </Box>
                
                {/* Upload Progress for Photos */}
                {Object.keys(uploadProgress).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            Uploading Files...
                        </Typography>
                        <Stack spacing={1}>
                            {Object.entries(uploadProgress).map(([fileName, progress]) => (
                                <Paper key={fileName} sx={{ p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {fileName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={progress} 
                                                sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                            />
                                            <Typography variant="caption" sx={{ fontWeight: 500, minWidth: 40 }}>
                                                {progress}%
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                )}
                
                {formData.photo_urls && formData.photo_urls.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            Photos ({formData.photo_urls.length})
                        </Typography>
                        <Stack spacing={1}>
                            {formData.photo_urls.map((file, index) => (
                                <Paper key={index} sx={{ p: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar sx={{ bgcolor: '#2196f3' }}>
                                            <PhotoCameraIcon />
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {file.name || `Photo ${index + 1}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => handlePreviewFile(file)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => removeFile(index, 'photos')} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Grid>
            
            {/* Document Upload */}
            <Grid item xs={12} md={6}>
                <Box 
                    sx={{ 
                        p: 3, 
                        border: dragOver ? '1px dashed #2196f3' : '1px dashed #ccc', 
                        borderRadius: 2,
                        textAlign: 'center',
                        backgroundColor: dragOver ? '#f3f8ff' : '#f9f9f9',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                    }}
                    onDrop={(e) => handleDrop(e, 'documents')}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onClick={() => document.getElementById('doc-upload').click()}
                >
                    <AttachFileIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        Upload Documents
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Drag & drop or click to select multiple documents
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Supports: PDF, DOC, XLS, COAs (Max 20MB each)
                    </Typography>
                    <input
                        id="doc-upload"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files, 'documents')}
                    />
                </Box>
                
                {formData.document_urls && formData.document_urls.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            Documents ({formData.document_urls.length})
                        </Typography>
                        <Stack spacing={1}>
                            {formData.document_urls.map((file, index) => (
                                <Paper key={index} sx={{ p: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar sx={{ bgcolor: '#ff9800' }}>
                                            <DescriptionIcon />
                                        </Avatar>
                                        <Box flex={1}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {file.name || `Document ${index + 1}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => handlePreviewFile(file)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => removeFile(index, 'documents')} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Grid>

            {/* Sampling Summary */}
            {samplingComplete && (
                <Grid item xs={12}>
                    <Alert 
                        severity="success"
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.success.main
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CheckCircleIcon />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                ✓ Sample Documentation Complete
                            </Typography>
                        </Stack>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Sample Location:</strong> {formData.sample_location}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Tested By:</strong> {formData.tested_by_name} ({formData.tested_by_role})
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <strong>Test Date:</strong> {dayjs(formData.test_datetime).format(batchTimeFormat === '24hour' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY h:mm A')}
                            </Typography>
                            {(formData.photo_urls?.length > 0 || formData.document_urls?.length > 0) && (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    <strong>Attachments:</strong> {(formData.photo_urls?.length || 0)} photos, {(formData.document_urls?.length || 0)} documents
                                </Typography>
                            )}
                        </Box>
                    </Alert>
                </Grid>
            )}

            {/* File Preview Dialog */}
            <Dialog 
                open={filePreviewOpen} 
                onClose={() => setFilePreviewOpen(false)} 
                maxWidth="md" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                    File Preview
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                    {previewFile && (
                        <Box sx={{ textAlign: 'center' }}>
                            {previewFile.type?.startsWith('image/') ? (
                                <img 
                                    src={previewFile.url} 
                                    alt={previewFile.name}
                                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                                />
                            ) : (
                                <Box sx={{ p: 4 }}>
                                    <DescriptionIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
                                    <Typography variant="h6">{previewFile.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Document preview not available
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => setFilePreviewOpen(false)}
                        sx={{
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[400], 0.1)
                            }
                        }}
                    >
                        Close
                    </Button>
                    {previewFile?.url && (
                        <Button 
                            startIcon={<DownloadIcon />} 
                            href={previewFile.url} 
                            download={previewFile.name}
                            sx={{
                                fontWeight: 500,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                                color: 'white',
                                borderRadius: 3,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.7)})`
                                }
                            }}
                        >
                            Download
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            </Grid>
        </Box>
    );
} 